import React from 'react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import { Modal } from 'ui/base/modal';
import { TextInput as SearchInput } from 'ui/base/input';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { BackButton } from 'ui/base/button/back_button';
import { ScrollableArea } from 'ui/base/page/scrollable_area';

import { CountryStore } from './country_store';
import baseStyles from './country_input.module.css';

import 'flag-icons/css/flag-icons.min.css';

import {
  CountryDetails,
  countries,
  getLocalNameByCode,
  getCountryByDialCode,
} from 'services/base/countries';

type CountryInputStyles = {
  countryButton: string;
  countryFlag: string;
  countryName: string;
  placeholder: string;
  textInput: string;
  modalContainer: string;
  column: string;
  headerContainer: string;
  listContainer: string;
  listItem: string;
  listItemFlag: string;
  listItemDialCode: string;
  listItemName: string;
};

const themedStyles: ThemedStyles<CountryInputStyles> = {
  [Theme.DAYLIGHT]: {
    countryButton: baseStyles.daylightcountryButton,
    countryFlag: baseStyles.countryFlag,
    countryName: baseStyles.countryName,
    placeholder: baseStyles.daylightPlaceholder,
    textInput: baseStyles.daylightTextInput,
    modalContainer: baseStyles.modalContainer,
    column: baseStyles.column,
    headerContainer: baseStyles.headerContainer,
    listContainer: baseStyles.listContainer,
    listItem: baseStyles.listItem,
    listItemFlag: baseStyles.listItemFlag,
    listItemDialCode: baseStyles.listItemDialCode,
    listItemName: baseStyles.listItemName,
  },
  [Theme.MIDNIGHT]: {
    countryButton: baseStyles.midnightcountryButton,
    countryFlag: baseStyles.countryFlag,
    countryName: baseStyles.countryName,
    placeholder: baseStyles.midnightPlaceholder,
    textInput: baseStyles.midnightTextInput,
    modalContainer: baseStyles.modalContainer,
    column: baseStyles.column,
    headerContainer: baseStyles.headerContainer,
    listContainer: baseStyles.listContainer,
    listItem: baseStyles.listItem,
    listItemFlag: baseStyles.listItemFlag,
    listItemDialCode: baseStyles.listItemDialCode,
    listItemName: baseStyles.listItemName,
  },
};

type FilteredCountryListProps = {
  store: CountryStore;
};

const FilteredCountryList = observer(({ store }: FilteredCountryListProps) => {
  const styles = useStyles(themedStyles);

  const onSelected = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, country: CountryDetails) => {
    event.stopPropagation();

    // HACK: When the dial code is the same as the old value, the CountryButton
    //       refuses to re-render even if the country name is different for
    //       some reason. Clearing the store value first before setting the
    //       appropriate value gets around this.
    store.country.setValue(''); store.country.setValue(country.baseName);
    store.prefix.setValue(''); store.prefix.setValue(country.dialCode);

    store.modal.hideModal();
  };

  return (
    <div className={styles.listContainer}>
      {
        countries.filter((item) => {
          return (getLocalNameByCode(item.code) ?? item.baseName).toLowerCase().indexOf(store.search.value.trim().toLowerCase()) >= 0;
        }).map((country, index) => {
          return (
            <li key={index} className={styles.listItem}
                onClick={(event) => onSelected(event, country)}>
              <span className={classNames(styles.listItemFlag, 'fi', `fi-${country.code.toLowerCase()}`)}></span>
              <span className={styles.listItemDialCode}>{country.dialCode}</span>
              <span className={styles.listItemName}>{getLocalNameByCode(country.code) ?? country.baseName}</span>
            </li>
          );
        })
      }
    </div>
  );
});

type CountryModalContentProps = {
  placeholder: string;
  store: CountryStore;
};

const CountryModalContent = ({ store, placeholder }: CountryModalContentProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.modalContainer}>
      <ScrollableArea>
        <div className={styles.column}>
          <div className={styles.headerContainer}>
            <BackButton onClick={store.modal.hideModal}/>
            <SearchInput
              store={store.search}
              placeholder={placeholder}
              className={styles.textInput}
              name='country-search'
              id='country-search'
            />
          </div>
          <FilteredCountryList store={store}/>
        </div>
      </ScrollableArea>
    </div>
  );
};

type CountryButtonProps = {
  placeholder: string;
  store: CountryStore;
};

const CountryButton = ({ store, placeholder }: CountryButtonProps) => {
  const styles = useStyles(themedStyles);
  const handleShow = React.useCallback(() => {
    runInAction(() => {
      store.search.value = '';
      store.modal.showModal();
    });
  }, [store]);

  if (store.country.value !== '') {
    const country = getCountryByDialCode(store.prefix.value, store.country.value);
    return (
      <button type='button' className={styles.countryButton} onClick={handleShow}>
        <span className={classNames(styles.countryFlag, 'fi', `fi-${country?.code.toLowerCase()}`)}></span>
        <span className={styles.countryName}>{country ? (getLocalNameByCode(country.code) ?? country.baseName) : store.country.value}</span>
      </button>
    );
  } else {
    return (
      <button type='button' className={classNames(styles.countryButton, styles.placeholder)} onClick={handleShow}>
        {placeholder}
      </button>
    );
  }
};

type CountryInputProps = {
  placeholder: string;
  store: CountryStore;
};

export const CountryInput = ({ store, placeholder }: CountryInputProps) => {
  return (
    <>
      <Modal store={store.modal}>
        <CountryModalContent store={store} placeholder={placeholder}/>
      </Modal>
      <CountryButton placeholder={placeholder} store={store}/>
    </>
  );
};